import { Box, FormControl, FormControlLabel, FormLabel as MuiFormLabel, Radio, RadioGroup } from '@mui/material'
import { color } from 'config/color'
import { ErrorText } from 'components/domain'
import type React from 'react'
import { Controller, type Control, type FieldErrors } from 'react-hook-form'

interface HorizontalRadioGroupProps {
    name: string
    control: Control<any>
    label: string
    options: Array<{ label: string, value: string }>
    defaultValue?: string
    errors: FieldErrors<any>
}

const HorizontalRadioGroup: React.FC<HorizontalRadioGroupProps> = ({
    name,
    control,
    label,
    options,
    defaultValue,
    errors
}) => {
    const groupLabelId = `radio-group-label-${name}`
    const errorId = `error-${name}`
    const hasError = errors[name] != null

    return (
        <Box mb={2}>
            <FormControl component="fieldset" fullWidth error={hasError}>
                <MuiFormLabel
                    id={groupLabelId}
                    component="legend"
                    sx={{
                        fontSize: 12,
                        color: color.grey[700],
                        textTransform: 'uppercase',
                        letterSpacing: 2,
                        marginBottom: 1,
                        whiteSpace: 'normal',
                        wordWrap: 'break-word'
                    }}
                >
                    {label.toUpperCase()}
                </MuiFormLabel>
                <Controller
                    name={name}
                    control={control}
                    defaultValue={defaultValue ?? ''}
                    render={({ field }) => (
                        <RadioGroup
                            {...field}
                            row
                            aria-labelledby={groupLabelId}
                            aria-invalid={hasError}
                            aria-describedby={hasError ? errorId : undefined}
                        >
                            {options.map((option, index) => (
                                <FormControlLabel
                                    key={index}
                                    value={option.value}
                                    control={<Radio />}
                                    label={option.label}
                                />
                            ))}
                        </RadioGroup>
                    )}
                />
                {hasError && (
                    <ErrorText id={errorId} role="alert">
                        {String(errors[name]?.message)}
                    </ErrorText>
                )}
            </FormControl>
        </Box>
    )
}

export default HorizontalRadioGroup
